import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { connectApi } from '@app/api';
import {
  selectActiveCompanyId,
  selectActiveCompany,
  selectEmployees,
  selectPersonId,
  selectPrimaryUser,
} from '@features/workspace/store/selectors';
import {
  setIsBankAccountsLoading,
  setIsUsersLoading,
  setIsEmployeesLoading,
  setIsCompanyDocumentsLoading,
} from '@features/workspace/store';
import { IRootState } from '@app/store';

export const fetchUser = createAsyncThunk<any, any, { state: IRootState }>(
  'user/fetch',
  async (userId, { getState }) => {
    const primaryUserId = selectPrimaryUser(getState())?.id;

    if (!userId && primaryUserId) {
      userId = primaryUserId;
    }
    const [user, userAddress] = await Promise.all([
      connectApi.user.get(userId).then(({ data }) => data.payload),
      connectApi.user
        .getAddress(userId)
        .then(({ data }) =>
          Array.isArray(data.payload) ? data.payload[0] : data.payload
        ),
    ]);

    return {
      ...user,
      address: userAddress,
    };
  }
);

export const fetchEmployee = createAsyncThunk<any, any, { state: IRootState }>(
  'employee/fetch',
  async ({ companyId, userId }, { getState }) => {
    return connectApi.employee
      .get(companyId, userId)
      .then(({ data }) => data.payload);
  }
);

export const fetchCompanies = createAsyncThunk<
  any,
  any,
  { state: IRootState; rejectValue: { status?: number } }
>(
  'company/fetch-person-assoc',
  async (personId, { getState, rejectWithValue }) => {
    if (!personId) {
      personId = selectPersonId(getState());
    }
    try {
      const companyIds = (await connectApi.company.getListByPerson(personId))
        ?.data?.payload;

      return await Promise.all(
        companyIds.map(async (companyId: string) => {
          const [company, companyAddress] = await Promise.all([
            connectApi.company.get(companyId).then(({ data }) => data.payload),
            connectApi.company
              .getAddress(companyId)
              .then(({ data }) =>
                Array.isArray(data.payload) ? data.payload[0] : data.payload
              ),
          ]);

          return {
            ...company,
            address: companyAddress,
          };
        })
      );
    } catch (err) {
      // Keep the HTTP status: the serialized error createAsyncThunk would
      // otherwise reject with doesn't carry it, and callers need it to tell
      // "not signed in" and "no access" apart from "no company"
      return rejectWithValue({ status: (err as AxiosError)?.response?.status });
    }
  }
);

export const fetchCompany = createAsyncThunk<any, any, { state: IRootState }>(
  'company/fetch',
  async (companyId, { getState }) => {
    if (!companyId) {
      companyId = selectActiveCompanyId(getState());
    }
    const [company, companyAddress] = await Promise.all([
      connectApi.company.get(companyId).then(({ data }) => data.payload),
      connectApi.company
        .getAddress(companyId)
        // TODO: get rid of isArray check, we expect object but not collection after api fix
        .then(({ data }) =>
          Array.isArray(data.payload) ? data.payload[0] : data.payload
        ),
    ]);

    return {
      ...company,
      address: companyAddress,
    };
  }
);

export const fetchBankAccounts = createAsyncThunk<
  any,
  any,
  { state: IRootState }
>('accounts/fetch', async (companyId, { getState, dispatch }) => {
  dispatch(setIsBankAccountsLoading(true));
  if (!companyId) {
    companyId = selectActiveCompany(getState())?.id;
  }

  return (await connectApi.bankAccount.getAll(companyId))?.data?.payload;
});

export const filterCompaniesByCreditCompanyId = createAsyncThunk<
  any,
  string,
  { state: IRootState }
>('companies-by-credit-company-id/filter', async (creditApiCompanyId) => {
  return (await connectApi.company.filterByCreditCompanyId(creditApiCompanyId))
    ?.data?.payload?.data;
});

export const fetchCompanyFundingApplications = createAsyncThunk<
  any,
  any,
  { state: IRootState }
>(
  'company-funding-application/fetch',
  async (creditApiCompanyId, { getState }) => {
    if (!creditApiCompanyId) {
      creditApiCompanyId = selectActiveCompany(getState())?.creditApiCompanyId;
    }

    return (
      await connectApi.creditApi.getFundingApplication(creditApiCompanyId)
    )?.data?.payload?.data;
  }
);

export const fetchAdvance = createAsyncThunk<
  any,
  string,
  { state: IRootState }
>('advance/fetch', async (id) => {
  return (await connectApi.creditApi.getAdvance(id))?.data?.payload;
});

export const fetchCompanyEmployees = createAsyncThunk<
  any,
  any,
  { state: IRootState }
>('company-employees/fetch', async (companyId, { getState, dispatch }) => {
  if (!companyId) {
    companyId = selectActiveCompany(getState())?.id;
  }
  dispatch(setIsEmployeesLoading(true));

  return (await connectApi.company.getEmployees(companyId))?.data?.payload;
});

export const fetchCompanyDocuments = createAsyncThunk<
  any,
  any,
  { state: IRootState }
>('company-documents/fetch', async (none, { getState, dispatch }) => {
  const companyId = selectActiveCompanyId(getState());

  dispatch(setIsCompanyDocumentsLoading(true));

  return (await connectApi.document.getDocumentsByCompany(companyId ?? ''))
    ?.data?.payload;
});

export const fetchUserDocuments = createAsyncThunk<
  any,
  string,
  { state: IRootState }
>('documents/fetch', async (userId) => {
  return (await connectApi.document.getDocumentsByUser(userId))?.data?.payload;
});

export const downloadDocument = createAsyncThunk<
  any,
  { resourceId: any; name: any },
  { state: IRootState }
>('documents/download', async ({ resourceId, name }) => {
  await connectApi.document.downloadDocument(resourceId, name);
});

export const fetchCompanyUsers = createAsyncThunk<
  any,
  any,
  { state: IRootState }
>('company/fetch-users', async (none, { getState, dispatch }) => {
  const companyId = selectActiveCompanyId(getState());

  dispatch(setIsUsersLoading(true));
  await dispatch(fetchCompanyEmployees(companyId));
  const employees = selectEmployees(getState());

  if (employees?.length) {
    await Promise.all(
      employees.map((employee) =>
        Promise.all([
          dispatch(fetchUser(employee.userId)),
          dispatch(fetchUserDocuments(employee.userId)),
        ])
      )
    );
  }
  dispatch(setIsUsersLoading(false));

  return employees;
});
